import { HTTP_STATUS_SUCCESS_CODES } from "./constants/apis";

/**
 * Checks if an HTTP request is successful based on the status code.
 *
 * @param {number} statusCode - The HTTP status code to check.
 * @returns {boolean} True if the status code indicates success, otherwise false.
 */
export function isRequestSuccessful(statusCode: number) {
  return Object.values(HTTP_STATUS_SUCCESS_CODES).includes(statusCode);
}

/**
 * Extracts an emoji from the beginning of a text string.
 *
 * @example
 * extractEmojiAndText("🏠 Feed")
 * // { emoji: "🏠", text: "Feed" }
 *
 * @example
 * extractEmojiAndText("Feed")
 * // { emoji: null, text: "Feed" }
 */
export const extractEmojiAndText = (text: string) => {
  const match = text.match(
    /^(\p{Emoji_Presentation}|\p{Extended_Pictographic})+/u,
  );

  if (match) {
    const emoji = match[0];
    const cleanText = text.replace(match[0], "").trim();
    return { emoji, text: cleanText };
  }

  return { emoji: null, text };
};

/**
 * Sets the opacity of an RGB or RGBA color string.
 *
 * @example
 * setOpacity("rgb(62, 119, 69)", 0.1)
 * // "rgba(62, 119, 69, 0.1)"
 *
 * @example
 * setOpacity("rgba(62, 119, 69, 0.5)", 0.1)
 * // "rgba(62, 119, 69, 0.1)"
 */
export const setOpacity = (color: string, opacity: number) => {
  // Handle rgb(r, g, b) format
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${opacity})`;
  }

  // Handle rgba(r, g, b, a) format
  const rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/);
  if (rgbaMatch) {
    return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${opacity})`;
  }

  // Return original if format not recognized
  return color;
};

/**
 * Extracts text content from a React node (string, number, or React element).
 * Recursively traverses React elements to extract text from children.
 */
export const getTextFromReactNode = (node: any): string | null => {
  if (node === null || node === undefined) {
    return null;
  }

  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getTextFromReactNode).filter(Boolean).join("");
  }

  if (node?.props?.children) {
    return getTextFromReactNode(node.props.children);
  }

  return null;
};
